// @flow strict
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useHistory} from 'src/rerouter';
import {useSelector} from 'react-redux';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import {useWebAndExtRouterControl} from 'src/components/micro-apps-extension/hooks/useWebAndExtRouterControl';
// $FlowFixMe[nonstrict-import]
import {useReleaseFlag} from 'src/hooks/product-flags';
import {useExtensionProductsList} from './hooks/useExtensionProductsList';
import {AnalyticsService} from 'src/analytics';
import {SideMenuLink} from '@spaced-out/ui-design-system/lib/components/SideMenuLink';
import SenseLogo from 'src/images/sense-new-logo.svg';
import SupportLinks from './support-links.jsx';
import {classify} from 'src/utils/classify';
import css from './extension-side-nav.css';


const routeToProduct = {
  broadcast: '/messages/hv-broadcasts',
  liveAgentTransfer: '/messages/queues',
  messaging: '/messages',
  contacts: '/contacts',
  referral: '/referralv2?source=ext',
  senseai: '/sense-ai-panel?source=ext',
  meetings: '/meetings',
  jobs: '/jobs/all',
  voiceflow: '/voiceflow/dashboard',
};

const ExtensionSideNav = (): React.Node => {
  const router = useHistory();
  const {changeLocation} = useWebAndExtRouterControl();
  const {t} = useLabelTranslation();

  const {isExtension, isClientIframe} = useEnvironmentContext();
  const isMicroAppsEnabled = useReleaseFlag(
    'messagingEnableMicroappsInExtension',
  );
  const agency = useSelector((state) => state.agency.agency.name);
  const agencies = ['HCAHealthcare', 'HCA-Infor-Sandbox', 'HCA-Infor-UAT'];
  const trackHCAEvent = agencies.includes(agency);
  // useExtensionProductsList checks permissions for products and provides list of product route names
  // ex: messaging, referrals, senseai which are part of routeToProduct
  const {productsList} = useExtensionProductsList();

  // we need this logic to redirect to a product with external clicks from ATS
  const pathName = router.location.pathname;
  const selectedProductKey =
    Object.keys(routeToProduct).find((route) =>
      pathName.includes(routeToProduct[route].split('?')[0]),
    ) || '';

  const [selectedProduct, setSelectedProduct] =
    React.useState(selectedProductKey);

  const routeToLocation = (key) => {
    changeLocation(router, routeToProduct[key]);
  };

  const customTitleMap = {
    messaging: t('Messaging', 'Messaging'),
    broadcast: t('Broadcast', 'Broadcast'),
    contacts: t('Contacts', 'Contacts'),
    liveAgentTransfer: t('Live_Agent_Transfer', 'Live Agent Transfer'),
    referral: t('Referrals', 'Referrals'),
    senseai: t('AI_Copilot', 'AI Copilot'),
    meetings: t('Scheduling', 'Scheduling'),
    jobs: t('Jobs', 'Jobs'),
    screenedCandidates: t('Screened_Candidates', 'Screened Candidates'),
  };

  React.useEffect(() => {
    setSelectedProduct(selectedProductKey);
    if (trackHCAEvent) {
      AnalyticsService.track('HCA MEETING_SCHEDULE_EVENT change location', {
        selectedProductKey,
        productsList,
        line: 63,
        devNote: 'initial route through effect',
      });
    }

    if (isExtension && !selectedProductKey) {
      const firstProductInTheList = productsList.length > 0 && productsList[0];
      if (trackHCAEvent) {
        AnalyticsService.track('HCA MEETING_SCHEDULE_EVENT change location', {
          firstProductInTheList,
          selectedProductKey,
          productsList,
          line: 75,
          devNote: 'route to first product in the list',
        });
      }
      if (firstProductInTheList) {
        setSelectedProduct(firstProductInTheList);
        routeToLocation(firstProductInTheList);
      }
    }
  }, [selectedProductKey]);

  const handleOnChange = (e, selectedKey) => {
    setSelectedProduct(selectedKey);
    routeToLocation(selectedKey);
  };

  const hideExtensionSideNav =
    !isMicroAppsEnabled || !(isExtension || isClientIframe);

  return (
    <>
      {!hideExtensionSideNav ? (
        <div className={css.container}>
          <div className={css.senseLogo}>
            <SenseLogo />
          </div>
          <div className={css.sideNavLinks}>
            {productsList.map((product) => {
              return (
                <SideMenuLink
                  onChange={handleOnChange}
                  pageNameKey={product}
                  selectedValue={selectedProduct}
                  customTitle={customTitleMap[product]}
                  tooltip={{classNames: {tooltip: css.toolTip}}}
                  classNames={{
                    wrapper: classify({
                      [css.selectedDashboardLink]: selectedProduct === product,
                    }),
                  }}
                />
              );
            })}
          </div>
          <SupportLinks />
        </div>
      ) : null}
    </>
  );
};

export default ExtensionSideNav;
